import React from 'react';
import { Head, useForm } from '@inertiajs/react';
import AdminLayout from '../../../components/AdminLayout';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { ArrowLeft } from 'lucide-react';

interface BlogPost {
  id?: number;
  title: string;
  content: string;
  author?: string;
  published_at?: string;
  status?: string;
}

interface Props {
  blog_post: BlogPost;
  errors?: string[];
}

const NewBlogPost: React.FC<Props> = ({ blog_post, errors = [] }) => {
  const { data, setData, post, processing } = useForm({
    title: blog_post.title || '',
    content: blog_post.content || '',
    author: blog_post.author || '',
    published_at: blog_post.published_at || '',
    status: blog_post.status || 'draft'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post('/admin/blog_posts');
  };

  return (
    <AdminLayout title="Nouvel article">
      <Head title="Nouvel article - Admin" />
      
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={() => window.history.back()}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Nouvel article</h1>
              <p className="text-gray-600">Créer un nouvel article de blog</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <Card className="max-w-4xl">
          <CardHeader>
            <CardTitle>Informations de l'article</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {errors.length > 0 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                  <ul className="text-sm text-red-600 space-y-1">
                    {errors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Titre et auteur */}
                <div className="space-y-4">
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                      Titre *
                    </label>
                    <Input
                      id="title"
                      value={data.title}
                      onChange={(e) => setData('title', e.target.value)}
                      required
                      className="w-full"
                      placeholder="Titre de l'article"
                    />
                  </div>

                  <div>
                    <label htmlFor="author" className="block text-sm font-medium text-gray-700 mb-2">
                      Auteur
                    </label>
                    <Input
                      id="author"
                      value={data.author}
                      onChange={(e) => setData('author', e.target.value)}
                      className="w-full"
                      placeholder="Nom de l'auteur"
                    />
                  </div>
                </div>

                {/* Statut et date de publication */}
                <div className="space-y-4">
                  <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                      Statut
                    </label>
                    <Select value={data.status} onValueChange={(value) => setData('status', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un statut" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Brouillon</SelectItem>
                        <SelectItem value="published">Publié</SelectItem>
                        <SelectItem value="archived">Archivé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label htmlFor="published_at" className="block text-sm font-medium text-gray-700 mb-2">
                      Date de publication
                    </label>
                    <Input
                      id="published_at"
                      type="datetime-local"
                      value={data.published_at}
                      onChange={(e) => setData('published_at', e.target.value)}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Contenu */}
              <div>
                <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
                  Contenu *
                </label>
                <textarea
                  id="content"
                  value={data.content}
                  onChange={(e) => setData('content', e.target.value)}
                  required
                  rows={15}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Contenu de l'article..."
                />
              </div>

              <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => window.history.back()}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={processing}>
                  {processing ? 'Création...' : 'Créer l\'article'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default NewBlogPost; 
